import axios, { AxiosInstance } from 'axios';
import {
  LoginRequest,
  LoginResponse,
  FishingBoat,
  BoatListResponse,
  BoatStats,
  QueryParams,
  CreateBoatDto,
  UpdateBoatDto,
  BoatImage,
  CaptainListResponse,
  CaptainDetail,
  CaptainCreateResponse,
  UnassignedBoat,
  CaptainApplicationListResponse,
  CaptainApplication,
  ApproveApplicationResponse,
  AnnouncementListResponse,
  AnnouncementPopup,
} from '../types';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post<any>('/admin/auth/login', credentials);
    const data = response.data;

    // 백엔드 응답 형식을 React가 기대하는 형식으로 변환
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      user: {
        id: 0, // 백엔드가 반환하지 않음
        email: '', // 백엔드가 반환하지 않음
        username: data.username,
        name: '', // 백엔드가 반환하지 않음
        isAdmin: true, // 관리자 로그인이므로 true로 설정
      },
    };
  }

  async getBoats(params?: QueryParams): Promise<BoatListResponse> {
    const response = await this.client.get<BoatListResponse>('/admin/fishing-boat', { params });
    return response.data;
  }

  async getBoatStats(): Promise<BoatStats> {
    const response = await this.client.get<BoatStats>('/admin/fishing-boat/stats');
    return response.data;
  }

  async getBoat(id: number): Promise<FishingBoat> {
    const response = await this.client.get<FishingBoat>(`/admin/fishing-boat/${id}`);
    return response.data;
  }

  async createBoat(data: CreateBoatDto): Promise<FishingBoat> {
    const response = await this.client.post<FishingBoat>('/admin/fishing-boat', data);
    return response.data;
  }

  async updateBoat(id: number, data: UpdateBoatDto): Promise<FishingBoat> {
    const response = await this.client.put<FishingBoat>(`/admin/fishing-boat/${id}`, data);
    return response.data;
  }

  async deleteBoat(id: number): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/fishing-boat/${id}`);
    return response.data;
  }

  async batchDeleteBoats(ids: number[]): Promise<{ message: string; count: number }> {
    const response = await this.client.post('/admin/fishing-boat/batch-delete', { ids });
    return response.data;
  }

  async uploadImages(boatId: number, files: File[]): Promise<BoatImage[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images', file);
    });

    const response = await this.client.post<BoatImage[]>(
      `/admin/fishing-boat/${boatId}/images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async deleteImage(boatId: number, imageId: number): Promise<{ message: string; filename: string }> {
    const response = await this.client.delete(`/admin/fishing-boat/${boatId}/images/${imageId}`);
    return response.data;
  }

  async updateImageOrder(
    boatId: number,
    imageId: number,
    orderIndex: number
  ): Promise<{ message: string }> {
    const response = await this.client.put(`/admin/fishing-boat/${boatId}/images/${imageId}/order`, {
      orderIndex,
    });
    return response.data;
  }

  async checkDuplicateWebsite(websiteUrl: string, excludeId?: number): Promise<FishingBoat[]> {
    const params: any = { websiteUrl };
    if (excludeId) {
      params.excludeId = excludeId;
    }
    const response = await this.client.get<FishingBoat[]>('/admin/fishing-boat/check-duplicate', { params });
    return response.data;
  }

  async checkDuplicateName(name: string, excludeId?: number): Promise<FishingBoat[]> {
    const params: any = { name };
    if (excludeId) {
      params.excludeId = excludeId;
    }
    const response = await this.client.get<FishingBoat[]>('/admin/fishing-boat/check-duplicate-name', { params });
    return response.data;
  }

  // ── 민물낚시터 관리 ──

  async getFreshwaterSpots(params?: any): Promise<any> {
    const response = await this.client.get('/admin/freshwater-spot', { params });
    return response.data;
  }

  async getFreshwaterSpotStats(): Promise<any> {
    const response = await this.client.get('/admin/freshwater-spot/stats');
    return response.data;
  }

  async getFreshwaterSpot(id: number): Promise<any> {
    const response = await this.client.get(`/admin/freshwater-spot/${id}`);
    return response.data;
  }

  async updateFreshwaterSpot(id: number, data: any): Promise<any> {
    const response = await this.client.put(`/admin/freshwater-spot/${id}`, data);
    return response.data;
  }

  async deleteFreshwaterSpot(id: number): Promise<any> {
    const response = await this.client.delete(`/admin/freshwater-spot/${id}`);
    return response.data;
  }

  // ── 조황 관리 ──

  async getConditions(params?: any): Promise<any> {
    const response = await this.client.get('/admin/conditions', { params });
    return response.data;
  }

  async getConditionStats(): Promise<any> {
    const response = await this.client.get('/admin/conditions/stats');
    return response.data;
  }

  async getCondition(id: number): Promise<any> {
    const response = await this.client.get(`/admin/conditions/${id}`);
    return response.data;
  }

  async deleteCondition(id: number): Promise<any> {
    const response = await this.client.delete(`/admin/conditions/${id}`);
    return response.data;
  }

  async triggerCrawl(opts?: { limit?: number; boatIds?: number[] }): Promise<any> {
    const response = await this.client.post('/admin/conditions/crawl-now', opts || {});
    return response.data;
  }

  // ── 조황 게시판 보유 배 ──

  async getConditionBoards(params?: any): Promise<any> {
    const response = await this.client.get('/admin/conditions/boards/list', { params });
    return response.data;
  }

  async getConditionBoardStats(): Promise<any> {
    const response = await this.client.get('/admin/conditions/boards/stats');
    return response.data;
  }

  // ── 사장님(선주/선장) 계정 관리 ──

  async getCaptains(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<CaptainListResponse> {
    const response = await this.client.get<CaptainListResponse>(
      '/admin/captains',
      { params },
    );
    return response.data;
  }

  async getCaptain(id: number): Promise<CaptainDetail> {
    const response = await this.client.get<CaptainDetail>(
      `/admin/captains/${id}`,
    );
    return response.data;
  }

  async createCaptain(data: {
    username: string;
    name: string;
    email?: string;
    phone?: string;
    boatIds?: number[];
    newBoatNames?: string[];
  }): Promise<CaptainCreateResponse> {
    const response = await this.client.post<CaptainCreateResponse>(
      '/admin/captains',
      data,
    );
    return response.data;
  }

  async updateCaptain(
    id: number,
    data: {
      name?: string;
      email?: string;
      addBoatIds?: number[];
      removeBoatIds?: number[];
      isBlocked?: boolean;
      blockReason?: string;
    },
  ): Promise<CaptainDetail> {
    const response = await this.client.put<CaptainDetail>(
      `/admin/captains/${id}`,
      data,
    );
    return response.data;
  }

  async resetCaptainPassword(id: number): Promise<{ newPassword: string }> {
    const response = await this.client.post<{ newPassword: string }>(
      `/admin/captains/${id}/reset-password`,
    );
    return response.data;
  }

  async deleteCaptain(id: number): Promise<{ ok: true }> {
    const response = await this.client.delete<{ ok: true }>(
      `/admin/captains/${id}`,
    );
    return response.data;
  }

  async getUnassignedBoats(params?: {
    search?: string;
    limit?: number;
  }): Promise<UnassignedBoat[]> {
    const response = await this.client.get<UnassignedBoat[]>(
      '/admin/captains/unassigned-boats',
      { params },
    );
    return response.data;
  }

  // ── 사장님 신청 관리 ──

  async getCaptainApplications(params?: {
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CaptainApplicationListResponse> {
    const response = await this.client.get<CaptainApplicationListResponse>(
      '/admin/captain-applications',
      { params },
    );
    return response.data;
  }

  async getCaptainApplication(id: number): Promise<CaptainApplication> {
    const response = await this.client.get<CaptainApplication>(
      `/admin/captain-applications/${id}`,
    );
    return response.data;
  }

  async approveCaptainApplication(
    id: number,
    data: {
      username: string;
      boatIds?: number[];
      newBoatName?: string;
      newBoatRegion?: string;
    },
  ): Promise<ApproveApplicationResponse> {
    const response = await this.client.put<ApproveApplicationResponse>(
      `/admin/captain-applications/${id}/approve`,
      data,
    );
    return response.data;
  }

  async rejectCaptainApplication(
    id: number,
    reason: string,
  ): Promise<CaptainApplication> {
    const response = await this.client.put<CaptainApplication>(
      `/admin/captain-applications/${id}/reject`,
      { reason },
    );
    return response.data;
  }

  // ── 공지 팝업 ──

  async getAnnouncements(params?: {
    page?: number;
    pageSize?: number;
  }): Promise<AnnouncementListResponse> {
    const response = await this.client.get<AnnouncementListResponse>(
      '/admin/announcements',
      { params },
    );
    return response.data;
  }

  async getAnnouncement(id: number): Promise<AnnouncementPopup> {
    const response = await this.client.get<AnnouncementPopup>(
      `/admin/announcements/${id}`,
    );
    return response.data;
  }

  /** multipart. image 필드 optional. */
  async createAnnouncement(data: {
    title: string;
    content: string;
    priority?: number;
    showFrom: string;
    showUntil: string;
    isActive?: boolean;
    image?: File | null;
  }): Promise<AnnouncementPopup> {
    const form = new FormData();
    form.append('title', data.title);
    form.append('content', data.content);
    if (data.priority !== undefined) form.append('priority', String(data.priority));
    form.append('showFrom', data.showFrom);
    form.append('showUntil', data.showUntil);
    if (data.isActive !== undefined) form.append('isActive', String(data.isActive));
    if (data.image) form.append('image', data.image);
    const response = await this.client.post<AnnouncementPopup>(
      '/admin/announcements',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }

  async updateAnnouncement(
    id: number,
    data: {
      title?: string;
      content?: string;
      priority?: number;
      showFrom?: string;
      showUntil?: string;
      isActive?: boolean;
      image?: File | null;
      removeImage?: boolean;
    },
  ): Promise<AnnouncementPopup> {
    const form = new FormData();
    if (data.title !== undefined) form.append('title', data.title);
    if (data.content !== undefined) form.append('content', data.content);
    if (data.priority !== undefined) form.append('priority', String(data.priority));
    if (data.showFrom !== undefined) form.append('showFrom', data.showFrom);
    if (data.showUntil !== undefined) form.append('showUntil', data.showUntil);
    if (data.isActive !== undefined) form.append('isActive', String(data.isActive));
    if (data.image) form.append('image', data.image);
    if (data.removeImage) form.append('removeImage', 'true');
    const response = await this.client.put<AnnouncementPopup>(
      `/admin/announcements/${id}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }

  async deleteAnnouncement(id: number): Promise<{ ok: true }> {
    const response = await this.client.delete<{ ok: true }>(
      `/admin/announcements/${id}`,
    );
    return response.data;
  }
}

export default new ApiService();
